/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// React
import React, { useState, useEffect } from "react";
import { Controller } from "react-hook-form";
// icons
import BASESvgIcon from "../../../../../../@BASE/core/BASESvgIcon";
// material-ui components
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
// custom components
import NumberedHeading from "../../../../../shared-components/forms/NumberedHeading";
import MediaAttribution from "../shared/MediaAttribution";
import CCArray from "../shared/CCArray";
import MediaFocusPointsArray from "../shared/MediaFocusPointsArray";
import MediaQuestionsArray from "../shared/MediaQuestionsArray";
import EmentorArray from "../shared/EmentorArray";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const Listening = (props) => {
  const { control, formState, watch, setValue } = props.methods;
  const { errors } = formState;
  const { inputDir } = props;
  const [fileError, setFileError] = useState(null);

  const recreateSeconds = (second) => {
    if (second.includes(",")) {
      const split = second.split(",");
      return parseFloat(split[0] + "." + split[1]);
    }

    return second;
  };

  /**
   *
   * @param {string} time
   * @param {'hour' | 'minute' | 'second'} timePeriod
   * @returns {number | null}
   */
  const separateTimeStamp = (time, timePeriod) => {
    if (typeof time !== "string" || !time.includes(":")) {
      console.warn("Invalid time format. Expected HH:MM:SS format.");
      return null;
    }
    const timeArray = time.split(":");

    switch (timePeriod) {
      case "hour":
        return parseInt(timeArray[0]);
      case "minute":
        return parseInt(timeArray[1]);
      case "second":
        return recreateSeconds(timeArray[2]);
      default:
        return null;
    }
  };

  const validateFile = (file) => {
    if (file.type !== "application/json" && !file.name.includes(".srt")) {
      setFileError("Invalid file type. Please upload a JSON or SRT file.");
      return false;
    }
    return true;
  };

  const readSRT = (data) => {
    const blocksArray = data.split("\r\n\r\n");
    // clean up the array by removing empty strings
    const filteredBlocks = blocksArray.filter((block) => block.trim() !== "");

    const ccArray = filteredBlocks.map((block, i) => {
      try {
        const blockArray = block.split("\r\n");
        // if the blockArray has less than 2 items, it's not a valid block
        if (blockArray.length < 2) {
          return null;
        }

        // const index = blockArray[0];
        const timestamps = blockArray[1].split(" --> ");
        // if the timestamps array has more than 2 items, it's not a valid block
        if (timestamps.length !== 2) {
          return null;
        }

        const start_minute = separateTimeStamp(timestamps[0], "minute");
        const start_second = separateTimeStamp(timestamps[0], "second");
        const end_minute = separateTimeStamp(timestamps[1], "minute");
        const end_second = separateTimeStamp(timestamps[1], "second");
        const tl_text = blockArray.slice(2).join(" ");

        return { start_minute, start_second, end_minute, end_second, tl_text };
      } catch (err) {
        return null;
      }
    });
    // one last cleanup to remove any null items
    const validCCArray = ccArray.filter((item) => item !== null);

    setValue("content.cc", validCCArray);
  };

  const readJSON = (result) => {
    const data = JSON.parse(result);
    const ccArray = data.map((ccItem) => {
      const start_minute = separateTimeStamp(ccItem.start_time, "minute");
      const start_second = separateTimeStamp(ccItem.start_time, "second");
      const end_minute = separateTimeStamp(ccItem.end_time, "minute");
      const end_second = separateTimeStamp(ccItem.end_time, "second");
      const tl_text = ccItem.text;
      return { start_minute, start_second, end_minute, end_second, tl_text };
    });

    setValue("content.cc", ccArray);
    // storage(ccArray);
  };

  const getFile = (e) => {
    const file = e.target.files[0];

    if (!validateFile(file)) {
      return;
    }
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => {
      try {
        if (file.name.includes(".srt")) {
          readSRT(reader.result);
        }
        if (file.type === "application/json") {
          readJSON(reader.result);
        }
      } catch (err) {
        setFileError("Invalid file. Please upload a valid file.");
      }
    };
  };

  useEffect(() => {
    if (fileError) console.warn("File Error:", fileError);
  }, [fileError]);

  return (
    <div>
      <NumberedHeading
        number="1"
        title="Media"
        instructions="Fill in the data for the Media."
      />
      <Controller
        control={control}
        name="content.media"
        render={({ field }) => (
          <MediaAttribution
            targetObj="content.media"
            inputDir={inputDir}
            errors={errors.content?.media}
            {...{ control }}
          />
        )}
      />

      <Divider className="mt-16" />

      <NumberedHeading
        number="2"
        title="Start Time (Minute)"
        instructions="Enter a start time (minute) for the media"
      />
      <Controller
        control={control}
        name="content.core.start_minute"
        render={({ field }) => (
          <TextField
            {...field}
            className="mt-16 mb-16"
            label="Start Time (Minute)"
            placeholder="Start Time (Minute)"
            error={!!errors.core?.content?.start_minute}
            helperText={errors.core?.content?.start_minute?.message}
            fullWidth
            required
            type="number"
          />
        )}
      />

      <NumberedHeading
        number="3"
        title="Start Time (Second)"
        instructions="Enter a start time (second) for the media"
      />
      <Controller
        control={control}
        name="content.core.start_second"
        render={({ field }) => (
          <TextField
            {...field}
            className="mt-16 mb-16"
            label="Start Time (Second)"
            placeholder="Start Time (Second)"
            error={!!errors.core?.content?.start_second}
            helperText={errors.core?.content?.start_second?.message}
            fullWidth
            required
            type="number"
          />
        )}
      />

      <NumberedHeading
        number="4"
        title="End Time (Minute)"
        instructions="Enter an end time (minute) for the media"
      />
      <Controller
        control={control}
        name="content.core.end_minute"
        render={({ field }) => (
          <TextField
            {...field}
            className="mt-16 mb-16"
            label="End Time (Minute)"
            placeholder="End Time (Minute)"
            error={!!errors.core?.content?.end_minute}
            helperText={errors.core?.content?.end_minute?.message}
            fullWidth
            required
            type="number"
          />
        )}
      />

      <NumberedHeading
        number="5"
        title="End Time (Second)"
        instructions="Enter a end time (second) for the media"
      />
      <Controller
        control={control}
        name="content.core.end_second"
        render={({ field }) => (
          <TextField
            {...field}
            className="mt-16 mb-16"
            label="End Time (Second)"
            placeholder="End Time (Second)"
            error={!!errors.core?.content?.end_second}
            helperText={errors.core?.content?.end_second?.message}
            fullWidth
            required
            type="number"
          />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="6"
        title="Closed Caption File Upload"
        instructions="Upload a closed caption JSON or SRT file for the selected listening. Acceptable file types include .json and .srt."
      />

      <Button
        component="label"
        role={undefined}
        variant="contained"
        className="mt-16 mb-24"
        color="secondary"
        tabIndex={0}
        startIcon={<BASESvgIcon>heroicons-solid:upload</BASESvgIcon>}
      >
        Upload
        <VisuallyHiddenInput
          type="file"
          onChange={(e) => getFile(e)}
          accept=".json, .srt"
        />
      </Button>

      <NumberedHeading
        number="7"
        title="Closed Captions"
        instructions="Using the controls below, please add closed captions for the selected listening"
      />
      <Controller
        control={control}
        name="content.cc"
        render={({ field }) => (
          <CCArray inputDir={inputDir} {...{ control, errors }} />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="8"
        title="Focus Points"
        instructions="Using the controls below, please add focus points for the selected listening."
      />
      <Controller
        control={control}
        name="content.focus_points"
        render={({ field }) => (
          <MediaFocusPointsArray inputDir={inputDir} {...{ control, errors }} />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="9"
        title="Interactive Questions"
        instructions="Using the controls below, please add interactive questions for the selected listening."
      />
      <Controller
        control={control}
        name="content.questions"
        render={({ field }) => (
          <MediaQuestionsArray
            inputDir={inputDir}
            {...{ control, errors, watch }}
          />
        )}
      />

      <Divider className="my-16" />

      <NumberedHeading
        number="10"
        title="eMentor Notes"
        instructions="Using the controls below, add any supplemental content to this page."
      />
      <Controller
        control={control}
        name="content.eMentor"
        render={({ field }) => <EmentorArray {...{ control, errors }} />}
      />
    </div>
  );
};

export default Listening;
